/* usePushNotifications — React hook that owns the SW registration, VAPID
 * fetch, permission prompt, and PushSubscription ↔ backend sync.
 *
 * Returns {supported, permission, subscribed, loading, error, enable, disable}.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const SW_PATH = "/sw.js";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const out = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) out[i] = rawData.charCodeAt(i);
  return out;
}

function arrayBufferToBase64(buf) {
  return btoa(String.fromCharCode.apply(null, new Uint8Array(buf)));
}

export function usePushNotifications(API) {
  const supported = typeof window !== "undefined" && "serviceWorker" in navigator &&
    "PushManager" in window && "Notification" in window;

  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const checkSubscription = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch { /* silent */ }
  }, [supported]);

  useEffect(() => {
    if (!supported) return;
    // Register the SW once on mount
    navigator.serviceWorker.register(SW_PATH).then(checkSubscription).catch(() => {});
  }, [supported, checkSubscription]);

  const enable = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    setError(null);
    try {
      // Permission
      let perm = permission;
      if (perm !== "granted") {
        perm = await Notification.requestPermission();
        setPermission(perm);
      }
      if (perm !== "granted") throw new Error("Разрешението е отказано");

      // VAPID key
      const { data: cfg } = await axios.get(`${API}/push/vapid-public-key`);
      if (!cfg.publicKey) throw new Error("Липсва VAPID public key");

      // Register & subscribe
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      const sub = existing || await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(cfg.publicKey),
      });

      // Send to backend
      await axios.post(`${API}/push/subscribe`, {
        endpoint: sub.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(sub.getKey("p256dh")),
          auth: arrayBufferToBase64(sub.getKey("auth")),
        },
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
    } catch (e) {
      setError(e.message || "Грешка при активиране");
    } finally {
      setLoading(false);
    }
  }, [API, permission, supported]);

  const disable = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (e) {
      setError(e.message || "Грешка при деактивиране");
    } finally {
      setLoading(false);
    }
  }, [API, supported]);

  const sendTest = useCallback(async () => {
    try { await axios.post(`${API}/push/test`, {}); }
    catch (e) { setError(e?.response?.data?.detail || "Тестът не успя"); }
  }, [API]);

  return { supported, permission, subscribed, loading, error, enable, disable, sendTest };
}
